import React from 'react'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { LmsTrainingActionsCell } from 'src/components/lms-training/grid/LmsTrainingActionsCell'
import { LmsTrainingLinkCell } from 'src/components/lms-training/grid/LmsTrainingLinkCell'
import type { LmsTrainingPlain } from 'src/interfaces/collectStepReference'
import { formatDateOnly, parseUnixTimestamp } from 'src/utils/grid'
import { lmsTrainingTableHeaderTitles } from './constants'
import { LmsTrainingsTableFields } from './enums'

/**
 * Wiring only — date handling comes from utils/grid, actions and the link from their own cells.
 *
 * A factory rather than a module constant: the actions column closes over the page's edit and
 * delete handlers — and over nothing else. Both are stable `useCallback` references, so the
 * memoized result stays referentially stable for the life of the page. The id currently being
 * deleted is read by LmsTrainingActionsCell itself, precisely so this array does not have to be
 * rebuilt mid-delete: a fresh array resets the grid's column widths and order. Callers must
 * still memoize the result.
 */
export const buildLmsTrainingColumns = (
  onEditLmsTraining: (lmsTraining: LmsTrainingPlain) => void,
  onDeleteLmsTraining: (lmsTraining: LmsTrainingPlain) => void,
  // `lms_trainings_manage` (ENG-1207 part 2). Edit and Delete both need it, so without it the
  // actions column is dropped rather than left as an empty menu.
  canManage: boolean,
): Array<GridColDef<LmsTrainingPlain>> => {
  const columns: Array<GridColDef<LmsTrainingPlain>> = [
    {
      field: LmsTrainingsTableFields.id,
      headerName: lmsTrainingTableHeaderTitles[LmsTrainingsTableFields.id],
      width: 300,
      sortable: true,
      display: 'flex',
      renderCell: (cellParams: GridRenderCellParams<LmsTrainingPlain>) => {
        // A uuid is too long for the column, so it truncates — `title` exposes the full value
        // on hover for anyone who needs to copy it out.
        return (
          <Typography variant="body2" css={tw`truncate`} title={cellParams.row.id}>
            {cellParams.row.id}
          </Typography>
        )
      },
    },
    {
      field: LmsTrainingsTableFields.externalId,
      headerName: lmsTrainingTableHeaderTitles[LmsTrainingsTableFields.externalId],
      width: 140,
      sortable: true,
      // `type: 'number'` so sorting is numeric rather than lexicographic (so 1001 < 1002 < 20001
      // rather than string order) — but alignment and rendering are overridden below, because
      // this is an identifier, not a quantity: the grid's default numeric treatment would
      // right-align it and render course 1001 as '1,001'.
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      display: 'flex',
      renderCell: (params: GridRenderCellParams<LmsTrainingPlain>) => {
        return <Typography variant="body2">{String(params.row.externalId)}</Typography>
      },
    },
    {
      field: LmsTrainingsTableFields.title,
      headerName: lmsTrainingTableHeaderTitles[LmsTrainingsTableFields.title],
      flex: 2,
      minWidth: 200,
      sortable: true,
      // Title identifies the row, so it is never offered for hiding in the manage-columns panel.
      hideable: false,
      // v8 renders cells in text mode by default; element children are only laid out (and
      // vertically centred) when the column opts into flex display.
      display: 'flex',
      renderCell: (params: GridRenderCellParams<LmsTrainingPlain>) => {
        return (
          <Typography variant="body2" css={tw`truncate`}>
            {params.row.title}
          </Typography>
        )
      },
    },
    {
      field: LmsTrainingsTableFields.description,
      headerName: lmsTrainingTableHeaderTitles[LmsTrainingsTableFields.description],
      flex: 3,
      minWidth: 200,
      sortable: true,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<LmsTrainingPlain>) => {
        const description = params.row.description?.trim()

        if (!description) {
          // A dash for a missing optional value is a cosmetic default, not fabricated content.
          return (
            <Typography variant="body2" css={tw`text-shades-500`}>
              —
            </Typography>
          )
        }

        return (
          <Typography variant="body2" css={tw`truncate`}>
            {description}
          </Typography>
        )
      },
    },
    {
      field: LmsTrainingsTableFields.url,
      headerName: lmsTrainingTableHeaderTitles[LmsTrainingsTableFields.url],
      width: 120,
      // Sorting a column of identical "Open" labels would be meaningless.
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<LmsTrainingPlain>) => {
        return <LmsTrainingLinkCell url={params.row.url} />
      },
    },
    {
      field: LmsTrainingsTableFields.createdAt,
      headerName: lmsTrainingTableHeaderTitles[LmsTrainingsTableFields.createdAt],
      width: 160,
      sortable: true,
      type: 'date',
      // Backend `createdAt` is unix SECONDS — parse first, then format the resulting Date.
      valueGetter: parseUnixTimestamp,
      valueFormatter: formatDateOnly,
    },
    {
      field: LmsTrainingsTableFields.actions,
      headerName: lmsTrainingTableHeaderTitles[LmsTrainingsTableFields.actions],
      width: 68,
      sortable: false,
      hideable: false,
      disableExport: true,
      align: 'right',
      headerAlign: 'right',
      display: 'flex',
      renderCell: (params: GridRenderCellParams<LmsTrainingPlain>) => {
        return (
          <LmsTrainingActionsCell
            lmsTraining={params.row}
            onEdit={onEditLmsTraining}
            onDelete={onDeleteLmsTraining}
          />
        )
      },
    },
  ]

  if (canManage) {
    return columns
  }

  return columns.filter((column) => {
    return column.field !== LmsTrainingsTableFields.actions
  })
}
